import type { AgentNodeData } from '@saaga/shared/interfaces/agency-node.interface'
import type { Node } from '@xyflow/react'
import { useCallback } from 'react'

import type { useAgencyFlow } from './agency-flow.hook'
import { REACT_FLOW_FIT_VIEW_ANIMATED } from './agency-flow-canvas.config'
import type { useAgencyFlowCanvasDialogs } from './use-agency-flow-canvas-dialogs.hook'

interface UseNodeHandlersParams {
  agencyFlow: ReturnType<typeof useAgencyFlow>
  dialogs: ReturnType<typeof useAgencyFlowCanvasDialogs>
  fitView: (options?: object) => void
  setIsDrawerOpen: (isOpen: boolean) => void
}

export const useAgencyFlowNodeHandlers = ({
  agencyFlow,
  dialogs,
  fitView,
  setIsDrawerOpen,
}: UseNodeHandlersParams) => {
  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node): void => {
      agencyFlow.onNodeClick(node.id)
      setIsDrawerOpen(true)
      dialogs.closeEdgePopover()
    },
    [agencyFlow, dialogs, setIsDrawerOpen]
  )
  const handleDrawerOpenChange = useCallback(
    (isOpen: boolean): void => {
      setIsDrawerOpen(isOpen)
      if (!isOpen) agencyFlow.clearSelection()
    },
    [agencyFlow, setIsDrawerOpen]
  )
  const handleSaveNode = useCallback(
    (nodeId: string, data: Partial<AgentNodeData>): void => agencyFlow.updateNodeData(nodeId, data),
    [agencyFlow]
  )
  const handleAddNode = useCallback((): void => {
    agencyFlow.addNode()
    setTimeout(() => fitView(REACT_FLOW_FIT_VIEW_ANIMATED), 50)
  }, [agencyFlow, fitView])
  const handleAutoLayout = useCallback((): void => {
    agencyFlow.applyLayout()
    setTimeout(() => fitView(REACT_FLOW_FIT_VIEW_ANIMATED), 50)
  }, [agencyFlow, fitView])

  return {
    handleNodeClick,
    handleDrawerOpenChange,
    handleSaveNode,
    handleAddNode,
    handleAutoLayout,
  }
}
